import { TooltipOption } from "echarts/types/dist/shared";
import { EvaChartStates } from "types/factory";

import {
  getSeriesTooltipDashedLine,
  getSeriesTooltipDiamond,
  getSeriesTooltipDot
} from "components/charts/utils";
import { getSeriesNameWithFormattedUwi } from "components/charts/utils/getSeriesNameWithFormattedUWI";
import { formatNumber } from "components/charts/utils/precisionUtils";

import {
  getForecastSourceTooltipLine,
  getTooltipPositionWithXFieldSelector
} from "./utils";

export function translateCrossPlotTooltipOptions(states: EvaChartStates): TooltipOption {
  const {
    hoverLegendItemRef,
    series,
    uwis,
    layout,
    bestFitToggle,
    defaultSignificantFigures,
    useStrictRounding,
    uwiCoordinates,
    chartTypeParam
  } = states;
  const xAxisValues = layout?.xAxis?.data;
  const xAxisIsDate = layout?.xAxis?.axisIsDate;
  const isXAxisQuarter = layout?.xAxis?.title?.includes("(Year-Qtr)");
  const isBestFitActive = bestFitToggle?.active;

  // Extract dataTypes for x and y axes
  const xDataType = chartTypeParam?.properties?.x?.dataType;
  const yDataType = chartTypeParam?.properties?.y?.dataType;

  const colorList = {};
  if (Array.isArray(series) && series.length > 0) {
    for (const s of series) {
      colorList[s.label] = s.style.hexColor;
    }
  }

  // Helper function to create type well tooltip
  const createTypeWellTooltip = (
    seriesName: string,
    color: string,
    xValue: number | string,
    yValue: number
  ) => {
    // Format x value based on type
    let formattedXValue: string;
    if (typeof xValue === "string") {
      // Already formatted date string
      formattedXValue = isXAxisQuarter ? convertMonthToQuarter(xValue) : xValue;
    } else if (typeof xValue === "number") {
      formattedXValue = formatNumber(
        xValue,
        defaultSignificantFigures,
        useStrictRounding,
        xDataType
      );
    } else {
      formattedXValue = String(xValue);
    }

    return (
      `<span style="color:#666; font-weight: 600;">Type Well</span>` +
      "<br/>" +
      `<span style="color:#666">${getSeriesTooltipDiamond(color)}${seriesName}</span>` +
      "<br/>" +
      `<p style="font-family: var(--fontMono);color:#666">${formattedXValue}&emsp;<span style="font-weight:700;color:#666;float:right;font-family: var(--fontMono);">${
        typeof yValue === "number"
          ? formatNumber(yValue, defaultSignificantFigures, useStrictRounding, yDataType)
          : yValue
      }</span></p>`
    );
  };

  // Helper function to create type well markLine tooltip (horizontal line at Y value)
  const createTypeWellMarkLineTooltip = (
    seriesName: string,
    color: string,
    yValue: number | string
  ) => {
    return (
      `<div style="line-height:1.4; padding:2px 3px;">` +
      `<span style="color:#666; font-weight: 600;">Type Well</span>` +
      `<br/>` +
      `<span style="display:inline; color:#666;">${getSeriesTooltipDashedLine(
        color
      )}${seriesName}</span>` +
      `<br/>` +
      `<span style="font-size:14px;font-weight:700;color:#666;font-family: var(--fontMono);">${
        typeof yValue === "number"
          ? formatNumber(yValue, defaultSignificantFigures, useStrictRounding, yDataType)
          : yValue
      }</span>` +
      `</div>`
    );
  };

  // Helper to check if a series is a type well
  const isTypeWellSeries = (seriesId: string) => seriesId?.includes("typewell");

  if (isBestFitActive) {
    return {
      trigger: "axis",
      hideDelay: 0,
      transitionDuration: 0,
      axisPointer: {
        type: "cross",
        triggerTooltip: false,
        snap: true,
        label: { show: true }
      },
      formatter: (param) => {
        // Handle markLine tooltip (type well horizontal lines when X is date/header field)
        // For trigger: "axis", param can be an array and markLine comes as first item
        const paramArray = Array.isArray(param) ? param : [param];
        const markLineItem = paramArray.find((p) => p?.componentType === "markLine");
        if (markLineItem) {
          const markLineData = markLineItem?.data;
          const markLineName = markLineData?.name ?? markLineItem?.name;
          const yValue = markLineData?.value ?? markLineData?.yAxis;
          const lineColor = markLineItem?.borderColor ?? markLineItem?.color;
          return createTypeWellMarkLineTooltip(markLineName, lineColor, yValue);
        }

        const hoveredItem = hoverLegendItemRef?.current;
        if (hoveredItem) {
          // Line of Best Fit
          if (hoveredItem?.includes("Line of Best Fit")) {
            const color =
              series.find((s) => s.label === hoveredItem)?.style.hexColor ??
              param[0].color;
            const equation = series.find((s) => s.label === hoveredItem)?.equation;
            const rSquared = series.find((s) => s.label === hoveredItem)?.rSquared;
            return createBestFitTooltip(hoveredItem, color, equation, rSquared);
            // Scatter points
          } else {
            if (param) {
              // param when using trigger: "axis" should always be an array
              const filteredItems = Array.isArray(param) ? param : [param];
              const series = filteredItems.length > 0 ? filteredItems[0] : null;
              if (!series) {
                return "";
              }
              // Handle type well series (data has only 2 values: [x, y])
              if (isTypeWellSeries(series.seriesId) && series.value?.length === 2) {
                return createTypeWellTooltip(
                  series.seriesName,
                  series.color,
                  series.value[0],
                  series.value[1]
                );
              }
              if (
                !series.value ||
                series.value.length < 3 ||
                series.value[2] === undefined
              ) {
                return "";
              }
              let xValue = series.value[0];
              const yValue = series.value[1];
              const uwiIndexPresent = series.value?.length === 3;
              const seriesName = series.seriesName;
              const uwi =
                series && Array.isArray(series.value) && series.value.length >= 3
                  ? uwis[series.value[2]]
                  : "";
              if (isXAxisQuarter) {
                xValue = convertMonthToQuarter(xValue);
              }
              const seriesColor = colorList[seriesName];
              if (xAxisIsDate && uwiIndexPresent && xAxisValues) {
                return (
                  `<span style="color:#666";>${getSeriesNameWithFormattedUwi(
                    uwi
                  )}</span>` +
                  getForecastSourceTooltipLine(uwiCoordinates, uwi) +
                  "<br/>" +
                  `<span style="color:#666";>${getSeriesTooltipDot(
                    seriesColor
                  )}${seriesName}</span>` +
                  "<br/>" +
                  `<p style="font-family: var(--fontMono);color:#666">${xValue}&emsp;<span style="font-weight:700;color:#666;float:right;font-family: var(--fontMono);";>${formatNumber(
                    yValue,
                    defaultSignificantFigures,
                    useStrictRounding,
                    yDataType
                  )}</span></p>`
                );
              } else if (!xAxisIsDate && uwiIndexPresent) {
                if (typeof xValue === "number" && typeof yValue === "number") {
                  return (
                    `<span style="color:#666";>${getSeriesNameWithFormattedUwi(
                      uwi
                    )}</span>` +
                    getForecastSourceTooltipLine(uwiCoordinates, uwi) +
                    "<br/>" +
                    `<span style="color:#666";>${getSeriesTooltipDot(
                      seriesColor
                    )}${seriesName}</span>` +
                    "<br/>" +
                    `<p style="font-family: var(--fontMono);color:#666">${formatNumber(
                      xValue,
                      defaultSignificantFigures,
                      useStrictRounding,
                      xDataType
                    )}&emsp;<span style="font-weight:700;color:#666;float:right;font-family: var(--fontMono);";>${formatNumber(
                      yValue,
                      defaultSignificantFigures,
                      useStrictRounding,
                      yDataType
                    )}</span></p>`
                  );
                } else if (typeof xValue === "string" && typeof yValue === "number") {
                  return (
                    `<span style="color:#666";>${getSeriesNameWithFormattedUwi(
                      uwi
                    )}</span>` +
                    getForecastSourceTooltipLine(uwiCoordinates, uwi) +
                    "<br/>" +
                    `<span style="color:#666";>${getSeriesTooltipDot(seriesColor)}${
                      series.seriesName
                    }</span>` +
                    "<br/>" +
                    `<p style="font-family: var(--fontMono);color:#666">${xValue}
                &emsp;<span style="font-weight:700;color:#666;float:right;font-family: var(--fontMono);";>${formatNumber(
                  yValue,
                  defaultSignificantFigures,
                  useStrictRounding,
                  yDataType
                )}</span></p>`
                  );
                }
              }
            }
          }
        } else {
          const firstSeries = param[0];
          if (
            firstSeries?.seriesName &&
            firstSeries.seriesName.includes("Line of Best Fit")
          ) {
            return null;
          } else {
            if (
              !firstSeries ||
              !firstSeries.value ||
              firstSeries.value.length < 3 ||
              firstSeries.value[2] === undefined
            ) {
              return "";
            }
            let xValue = firstSeries.value[0];
            const yValue = firstSeries.value[1];
            const uwiIndexPresent = firstSeries.value?.length === 3;
            const seriesName = firstSeries.seriesName;
            const uwi =
              firstSeries &&
              Array.isArray(firstSeries.value) &&
              firstSeries.value.length >= 3
                ? uwis[firstSeries.value[2]]
                : "";
            const seriesColor = colorList[seriesName];
            if (isXAxisQuarter) {
              xValue = convertMonthToQuarter(xValue);
            }
            if (xAxisIsDate && uwiIndexPresent && xAxisValues) {
              return (
                `<span style="color:#666";>${getSeriesNameWithFormattedUwi(uwi)}</span>` +
                getForecastSourceTooltipLine(uwiCoordinates, uwi) +
                "<br/>" +
                `<span style="color:#666";>${getSeriesTooltipDot(
                  seriesColor
                )}${seriesName}</span>` +
                "<br/>" +
                `<p style="font-family: var(--fontMono);color:#666">${xValue}&emsp;<span style="font-weight:700;color:#666;float:right;font-family: var(--fontMono);";>${formatNumber(
                  yValue,
                  defaultSignificantFigures,
                  useStrictRounding,
                  yDataType
                )}</span></p>`
              );
            } else if (!xAxisIsDate && param?.value?.length === 3) {
              if (typeof xValue === "number" && typeof yValue === "number") {
                return (
                  `<span style="color:#666";>${getSeriesNameWithFormattedUwi(
                    uwi
                  )}</span>` +
                  getForecastSourceTooltipLine(uwiCoordinates, uwi) +
                  "<br/>" +
                  `<span style="color:#666";>${getSeriesTooltipDot(
                    seriesColor
                  )}${seriesName}</span>` +
                  "<br/>" +
                  `<p style="font-family: var(--fontMono);color:#666">${formatNumber(
                    xValue,
                    defaultSignificantFigures,
                    useStrictRounding,
                    xDataType
                  )}&emsp;<span style="font-weight:700;color:#666;float:right;font-family: var(--fontMono);";>${formatNumber(
                    yValue,
                    defaultSignificantFigures,
                    useStrictRounding,
                    yDataType
                  )}</span></p>`
                );
              }
            }
          }
        }

        // Don't show tooltip if no valid content
        return "";
      },
      position: getTooltipPositionWithXFieldSelector
    };
  } else {
    // crosshair should not be shown when the chart is not in best fit mode
    return {
      trigger: "item",
      hideDelay: 0,
      transitionDuration: 0, // prevent tooltip from flickering left to right on render
      formatter: function (items) {
        // Handle markLine tooltip (type well horizontal lines when X is date/header field)
        if (items?.componentType === "markLine") {
          const markLineData = items?.data;
          const markLineName = markLineData?.name ?? items?.name;
          const yValue = markLineData?.value ?? markLineData?.yAxis;
          const lineColor = items?.borderColor ?? items?.color;
          return createTypeWellMarkLineTooltip(markLineName, lineColor, yValue);
        }

        const filteredItems = Array.isArray(items)
          ? items.filter(
              (item) =>
                item.seriesName.startsWith(hoverLegendItemRef?.current) &&
                item.value !== ""
            )
          : [items];

        const series = filteredItems.length > 0 ? filteredItems[0] : null;
        const uwiIndexPresent = items?.value?.length === 3;

        if (series) {
          // Handle type well series (data has only 2 values: [x, y])
          if (isTypeWellSeries(series.seriesId) && series.value?.length === 2) {
            return createTypeWellTooltip(
              series.seriesName,
              series.color,
              series.value[0],
              series.value[1]
            );
          }
          if (!series.value || series.value.length < 2) {
            return "";
          }
          let xValue = series.value[0];
          const yValue = series.value[1];
          const uwi =
            series && Array.isArray(series.value) && series.value.length >= 3
              ? uwis[series.value[2]]
              : "";
          const seriesColor = colorList[series.seriesName];
          if (isXAxisQuarter) {
            xValue = convertMonthToQuarter(xValue);
          }
          if (xAxisIsDate && uwiIndexPresent && xAxisValues) {
            return (
              `<span style="color:#666";>${getSeriesNameWithFormattedUwi(uwi)}</span>` +
              getForecastSourceTooltipLine(uwiCoordinates, uwi) +
              "<br/>" +
              `<span style="color:#666";>${getSeriesTooltipDot(seriesColor)}${
                series.seriesName
              }</span>` +
              "<br/>" +
              `<p style="font-family: var(--fontMono);color:#666">${xValue}&emsp;<span style="font-weight:700;color:#666;float:right;font-family: var(--fontMono);";>${formatNumber(
                yValue,
                defaultSignificantFigures,
                useStrictRounding,
                yDataType
              )}</span></p>`
            );
          } else if (!xAxisIsDate && items?.value?.length === 3) {
            if (typeof xValue === "number" && typeof yValue === "number") {
              return (
                `<span style="color:#666";>${getSeriesNameWithFormattedUwi(uwi)}</span>` +
                getForecastSourceTooltipLine(uwiCoordinates, uwi) +
                "<br/>" +
                `<span style="color:#666";>${getSeriesTooltipDot(seriesColor)}${
                  series.seriesName
                }</span>` +
                "<br/>" +
                `<p style="font-family: var(--fontMono);color:#666">${formatNumber(
                  xValue,
                  defaultSignificantFigures,
                  useStrictRounding,
                  xDataType
                )}&emsp;<span style="font-weight:700;color:#666;float:right;font-family: var(--fontMono);";>${formatNumber(
                  yValue,
                  defaultSignificantFigures,
                  useStrictRounding,
                  yDataType
                )}</span></p>`
              );
            } else if (typeof xValue === "string" && typeof yValue === "number") {
              return (
                `<span style="color:#666";>${getSeriesNameWithFormattedUwi(uwi)}</span>` +
                getForecastSourceTooltipLine(uwiCoordinates, uwi) +
                "<br/>" +
                `<span style="color:#666";>${getSeriesTooltipDot(seriesColor)}${
                  series.seriesName
                }</span>` +
                "<br/>" +
                `<p style="font-family: var(--fontMono);color:#666">${xValue}
                &emsp;<span style="font-weight:700;color:#666;float:right;font-family: var(--fontMono);";>${formatNumber(
                  yValue,
                  defaultSignificantFigures,
                  useStrictRounding,
                  yDataType
                )}</span></p>`
              );
            }
          }
        }

        // Return empty string if no tooltip content could be generated
        return "";
      },
      position: getTooltipPositionWithXFieldSelector
    };
  }
}

export function createBestFitTooltip(seriesName, color, equation, rSquared) {
  const header = `<p style="text-align:center;">
      ${getSeriesTooltipDot(color)} ${seriesName}
    </p>`;

  const rowContent = (label, data) =>
    `<tr>
      <td style="text-align:left;padding-right:30px;">${label}</td>
      <td style="font-weight:700;color:#666;text-align:right;font-family: var(--fontMono);">${data}</td>
    </tr>`;

  const contentRows = [
    rowContent("Equation:", equation),
    ...(rSquared ? [rowContent("R²:", rSquared.toFixed(4))] : [])
  ].join("");

  const body = `
    <table style="width:100%;">
      ${contentRows}
    </table>
    <span style="font-size:10px;">Calculated from data.</span>`;

  return `${header}${body}`;
}

export function convertMonthToQuarter(dateString) {
  const { year, month } = (() => {
    const [y, m] = dateString.split("-").map(Number);
    return { year: y, month: m };
  })();
  const quarter = Math.floor((month - 1) / 3) + 1;
  return `${year}-Q${quarter}`;
}
